import { lazy } from "react";
import { Routes } from "react-router";
import { Outlet, Route } from "react-router-dom";
import AuthGuard from "~Common/AuthGuard/AuthGuard.tsx";
import { PermissionNames } from "~enums/permissions";
import { AccessDeniedPage, NotFoundPage, ScopeErrorPage } from "~pages/ErrorPages";
import OrganizationTabsRoutes from "./OrganizationTabsRoutes";
import SeasonTabsRoutes from "./SeasonTabsRoutes.tsx";

const CompetitionSeasonList = lazy(() => import("~pages/Seasons/SeasonListPage/SeasonListPage"));
const DivisionListPage = lazy(() => import("~pages/Divisions/DivisionListPage/DivisionListPage.tsx"));
const LeaderCriteriaRuleListPage = lazy(() => import("~pages/LeaderCriteria/LeaderCriteriaRuleListPage/LeaderCriteriaRuleListPage"));
const LeagueManagePage = lazy(() => import("~pages/Leagues/LeagueManagePage/LeagueManagePage"));
const CompetitionManagePage = lazy(() => import("~pages/Competitions/CompetitionManagePage/CompetitionManagePage"));
const ManageClub = lazy(() => import("../Features/Club/ManageClub"));
const ManageConferences = lazy(() => import("../Features/Conferences/ManageConferences"));
const ManageDivision = lazy(() => import("../Features/Conferences/ManageDivision"));
const LeaderCriteriaManagePage = lazy(() => import("~pages/LeaderCriteria/LeaderCriteriaManagePage/LeaderCriteriaManagePage"));
const LeaderCriteriaRuleManagePage = lazy(() => import("~pages/LeaderCriteria/LeaderCriteriaRuleManagePage/LeaderCriteriaRuleManagePage"));
const ManageMatchProfile = lazy(() => import("../Features/MatchProfiles/ManageMatchProfile"));
const PersonProfile = lazy(() => import("~pages/Persons/PersonProfile/PersonProfile"));
const NewSeason = lazy(() => import("../Features/EditSeason/NewSeason/NewSeason"));
const ManageStandingConfigurations = lazy(() => import("../Features/StandingsConfigurations/ManageStandingConfiguration"));
const ManageVenue = lazy(() => import("../Features/Venues/ManageVenue"));
const ManageSite = lazy(() => import("../Features/Sites/ManageSite"));
const ManageTeam = lazy(() => import("../Features/Team/ManageTeam"));
const PermissionUserDetailsPage = lazy(() => import("~pages/Users/PermissionUserDetailsPage/PermissionUserDetailsPage"));
const PermissionUserManagePage = lazy(() => import("~pages/Users/PermissionUserManagePage/PermissionUserManagePage"));

const FixtureManagePage = lazy(() => import("../pages/Fixtures/FixtureManagePage"));
const BroadcastPage = lazy(() => import("../pages/Fixtures/BroadcastPage/BroadcastPage.tsx"));
const SeasonRosterListPage = lazy(() => import("~pages/Roster/SeasonRosterListPage/SeasonRosterListPage"));
const FixtureRosterListPage = lazy(() => import("~pages/Roster/FixtureRosterListPage/FixtureRosterListPage"));
const RosterImages = lazy(() => import("../Features/Roster/RosterImages/RosterImages"));
const StaffListPage = lazy(() => import("~pages/Staff/StaffListPage/StaffListPage"));
const EditStandings = lazy(() => import("../Features/Standings/EditStandings/EditStandings"));
const EditStandingsAdjustments = lazy(() => import("../Features/StandingsAdjustments/EditStandingsAdjustments"));
const StandingsAdjustments = lazy(() => import("../Features/StandingsAdjustments/StandingsAdjustments"));
const SeasonEntityManagePage = lazy(() => import("~pages/SeasonEntities/SeasonEntityManagePage/SeasonEntityManagePage"));
const SeasonStageManagePage = lazy(() => import("~pages/SeasonStages/SeasonStageManagePage/SeasonStageManagePage"));
const SeasonPoolManagePage = lazy(() => import("~pages/SeasonPools/SeasonPoolManagePage/SeasonPoolManagePage"));
const NotificationsPage = lazy(() => import("../pages/Notifications/NotificationsPage.tsx"));
const CurrentUserDetailsPage = lazy(() => import("../pages/Users/CurrentUserDetailsPage/CurrentUserDetailsPage.tsx"));
const SelectCustomerPage = lazy(() => import("~pages/StartupWizard/SelectCustomerPage"));
const SelectOrganizationPage = lazy(() => import("~pages/StartupWizard/SelectOrganizationPage"));

const ApplicationRoutes = () => (
  <Routes>
    <Route index element={<SelectCustomerPage />} />
    <Route path="403" element={<AccessDeniedPage />} />
    <Route path=":customerId" element={<Outlet />}>
      <Route path="select/organization" element={<SelectOrganizationPage />} />
      <Route path="notifications" element={<NotificationsPage />} />

      <Route path="admin/:sport/:organizationId/user-details" element={<CurrentUserDetailsPage />} />
      <Route path="admin/:sport/:organizationId/403" element={<AccessDeniedPage />} />
      <Route path="admin/:sport/:organizationId/scope-error" element={<ScopeErrorPage />} />
      {/*ADMIN ROUTES: ORG LEVEL*/}
      <Route
        path="admin/:sport/:organizationId/leagues/manage/:leagueId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.LEAGUE]}>
            <LeagueManagePage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/competition/manage/:competitionId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.COMPETITION]}>
            <CompetitionManagePage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/entity-groups/manage/:clubId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.ENTITY_GROUP]}>
            <ManageClub />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/conferences/manage/:dataId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.CONFERENCE]}>
            <ManageConferences />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/entities/manage/:entityId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.ENTITY]}>
            <ManageTeam />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/sites/manage/:dataId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.SITE]}>
            <ManageSite />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/venues/manage/:dataId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.VENUE]}>
            <ManageVenue />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/persons/manage/:personId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.PERSON]}>
            <PersonProfile />
          </AuthGuard>
        }
      />
      <Route
        path={"admin/:sport/:organizationId/standings-configuration/manage/:standingConfigurationId?"}
        element={
          <AuthGuard permissionKeys={[PermissionNames.STANDING_CONFIGURATION]}>
            <ManageStandingConfigurations />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/leaders-qualification/manage/:leaderCriteriaId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.LEADER_QUALIFIER]}>
            <LeaderCriteriaManagePage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/match-profiles/manage/:matchProfileId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.MATCH_PROFILE]}>
            <ManageMatchProfile />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/leaders-qualification/:leaderCriteriaId/qualifiers"
        element={
          <AuthGuard permissionKeys={[PermissionNames.LEADER_CRITERIA_SET]}>
            <LeaderCriteriaRuleListPage />
          </AuthGuard>
        }
      />
      <Route
        path={"admin/:sport/:organizationId/leaders-qualification/:leaderCriteriaId/qualifiers/manage"}
        element={
          <AuthGuard permissionKeys={[PermissionNames.LEADER_CRITERIA_SET]}>
            <LeaderCriteriaRuleManagePage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/conferences/:conferenceId/divisions"
        element={
          <AuthGuard permissionKeys={[PermissionNames.DIVISION]}>
            <DivisionListPage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/conferences/:conferenceId/divisions/manage/:divisionId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.DIVISION]}>
            <ManageDivision />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/permissions/manage/:userId"
        element={
          <AuthGuard permissionKeys={[PermissionNames.PERMISSIONS]}>
            <PermissionUserDetailsPage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/permissions/manage/:userId/permission/:permissionId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.PERMISSIONS]}>
            <PermissionUserManagePage />
          </AuthGuard>
        }
      />
      {/* SEASON LEVEL ROUTES */}

      {/* Season level tabs */}

      <Route
        path="admin/:sport/:organizationId/competition/:competitionId/seasons/"
        element={
          <AuthGuard permissionKeys={[PermissionNames.COMPETITION]}>
            <CompetitionSeasonList />
          </AuthGuard>
        }
      />

      <Route
        path="admin/:sport/:organizationId/competition/:competitionId/seasons/add"
        element={
          <AuthGuard permissionKeys={[PermissionNames.SEASON]}>
            <NewSeason />
          </AuthGuard>
        }
      />

      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/entities/:entityId"
        element={
          <AuthGuard permissionKeys={[PermissionNames.SEASON_ENTITY]}>
            <SeasonEntityManagePage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/stages/:stageCode"
        element={
          <AuthGuard permissionKeys={[PermissionNames.STAGE]}>
            <SeasonStageManagePage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/pools/:poolCode"
        element={
          <AuthGuard permissionKeys={[PermissionNames.POOL]}>
            <SeasonPoolManagePage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/entities/:entityId/roster"
        element={
          <AuthGuard permissionKeys={[PermissionNames.SEASON_ROSTER]}>
            <SeasonRosterListPage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/fixtures/:fixtureId/roster"
        element={
          <AuthGuard permissionKeys={[PermissionNames.FIXTURE_ROSTER]}>
            <FixtureRosterListPage />
          </AuthGuard>
        }
      />

      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/entities/:entityId/roster/images"
        element={
          <AuthGuard permissionKeys={[PermissionNames.SEASON_ROSTER]}>
            <RosterImages />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/entities/:entityId/staff"
        element={
          <AuthGuard permissionKeys={[PermissionNames.ROLES]}>
            <StaffListPage />
          </AuthGuard>
        }
      />

      <Route
        path="admin/:sport/:organizationId/standings/:standingId"
        element={
          <AuthGuard permissionKeys={[PermissionNames.STANDING]}>
            <EditStandings />
          </AuthGuard>
        }
      />

      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/standings/adjustments"
        element={
          <AuthGuard permissionKeys={[PermissionNames.STANDING_ADJUSTMENT]}>
            <StandingsAdjustments />
          </AuthGuard>
        }
      />
      <Route
        path={"admin/:sport/:organizationId/seasons/:seasonId/standings/adjustments/:standingsAdjustmentId"}
        element={
          <AuthGuard permissionKeys={[PermissionNames.STANDING_ADJUSTMENT]}>
            <EditStandingsAdjustments />
          </AuthGuard>
        }
      />

      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/fixtures/:fixtureId"
        element={
          <AuthGuard permissionKeys={[PermissionNames.FIXTURE]}>
            <FixtureManagePage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/fixtures/:fixtureId/broadcast"
        element={
          <AuthGuard permissionKeys={[PermissionNames.FIXTURE]}>
            <BroadcastPage />
          </AuthGuard>
        }
      />

      <Route path="admin/:sport/:organizationId/*" element={<OrganizationTabsRoutes />} />
      <Route path="admin/:sport/:organizationId/seasons/:seasonId/*" element={<SeasonTabsRoutes />} />
    </Route>

    <Route path="*" element={<NotFoundPage />} />
  </Routes>
);

export default ApplicationRoutes;
